import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import SaveRounded from '@mui/icons-material/SaveRounded';
import { Alert, Box } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { useNotify } from '../../../lib/notify';
import { DetailHeader, DetailSkeleton, ErrorState } from '../components/DocParts';
import { DocumentEditor, type EditorAction, type EditorValues } from '../components/DocumentEditor';
import { idem, salesKeys, type SalesOrder } from '../lib/api';
import { toOrderRequest } from '../lib/requests';
import { cartFromDocument, copyHeader } from '../lib/documents';
import { useCopy } from '../../../lib/useCopy';

function useAfterSave() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const notify = useNotify();
  return async (order: SalesOrder, verb: string) => {
    notify.success(`Order ${order.number} ${verb}`);
    qc.setQueryData([...salesKeys.orders, order.id], order);
    await qc.invalidateQueries({ queryKey: salesKeys.orders });
    void navigate(`/sales/orders/${order.id}`, { replace: true });
  };
}

export function OrderNewPage() {
  const { can, me } = useAuth();
  const after = useAfterSave();
  // Duplicate: the copied order's customer, lines, prices and notes, dated today, as a new draft.
  const copy = useCopy() as SalesOrder | null;
  const create = async (v: EditorValues, confirm: boolean) => {
    const order = await unwrap(
      api.POST('/sales-orders', {
        params: { header: idem() },
        body: { id: crypto.randomUUID(), ...toOrderRequest(v), confirm },
      }),
    );
    await after(order, confirm ? 'confirmed' : 'saved as draft');
  };
  const actions: EditorAction[] = [
    { label: 'Save draft', icon: <SaveRounded />, run: (v) => create(v, false) },
  ];
  if (can('sales.confirm'))
    actions.push({
      label: 'Save & confirm',
      primary: true,
      icon: <CheckCircleRounded />,
      run: (v) => create(v, true),
    });
  else actions[0] = { ...actions[0], primary: true } as EditorAction;
  return (
    <Box>
      <DetailHeader
        back={{ to: '/sales/orders', label: 'Orders' }}
        title={copy ? `New sales order · copy of ${copy.number}` : 'New sales order'}
        subtitle="Confirming reserves the stock; deliver and invoice from the order when ready."
      />
      {copy ? (
        <DocumentEditor
          key={copy.id}
          kind="order"
          actions={actions}
          initialCart={cartFromDocument(copy)}
          initialHeader={copyHeader(copy)}
        />
      ) : (
        <DocumentEditor
          kind="order"
          draftKey={`sm.draft.order.${me?.tenant.id ?? ''}`}
          actions={actions}
        />
      )}
    </Box>
  );
}

export function OrderEditPage() {
  const { id = '' } = useParams();
  const after = useAfterSave();
  const query = useQuery({
    queryKey: [...salesKeys.orders, id],
    queryFn: () => unwrap(api.GET('/sales-orders/{id}', { params: { path: { id } } })),
  });
  if (query.isLoading) return <DetailSkeleton />;
  if (query.isError || !query.data)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const order = query.data;
  if (order.status !== 'DRAFT')
    return (
      <Box>
        <DetailHeader
          back={{ to: `/sales/orders/${order.id}`, label: order.number }}
          title="Edit order"
        />
        <Alert severity="info">
          Only draft orders can be edited. This order is {order.status.toLowerCase()}.
        </Alert>
      </Box>
    );

  return (
    <Box>
      <DetailHeader
        back={{ to: `/sales/orders/${order.id}`, label: order.number }}
        title={`Edit ${order.number}`}
        subtitle="Draft — nothing is reserved until you confirm it."
      />
      <DocumentEditor
        key={order.version}
        kind="order"
        initialCart={cartFromDocument(order)}
        initialHeader={{
          contact: { id: order.contact.id, name: order.contact.name, phone: order.contact.phone },
          docDate: order.docDate,
          later: order.expectedDate ?? '',
          locationId: order.location.id,
          reference: order.reference ?? '',
          notes: order.notes ?? '',
        }}
        actions={[
          {
            label: 'Save changes',
            primary: true,
            icon: <SaveRounded />,
            run: async (v) => {
              const saved = await unwrap(
                api.PATCH('/sales-orders/{id}', {
                  params: { path: { id: order.id } },
                  body: { ...toOrderRequest(v), version: order.version },
                }),
              );
              await after(saved, 'saved');
            },
          },
        ]}
      />
    </Box>
  );
}
